import React, { useEffect, useState } from "react";
import { Save, Plus, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../AdminLayout";
import { adminAxios, formatErr } from "../api";
import { useAuth } from "../AuthContext";

const ROLES = ["super_admin", "manager", "support"];

export default function AdminSettings() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";

  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [newCity, setNewCity] = useState("");

  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const [usersPageSize, setUsersPageSize] = useState(25);
  const [newUser, setNewUser] = useState({ name: "", email: "", role: "support", password: "" });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    adminAxios.get("/settings").then((r) => setSettings(r.data)).catch((e) => toast.error(formatErr(e)));
    if (isSuperAdmin) {
      const qs = new URLSearchParams();
      qs.set("limit", String(usersPageSize));
      qs.set("skip", String((usersPage - 1) * usersPageSize));
      adminAxios.get(`/users?${qs.toString()}`).then((r) => { setUsers(r.data.items || []); setUsersTotal(r.data.total || 0); }).catch((e) => toast.error(formatErr(e)));
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    const qs = new URLSearchParams();
    qs.set("limit", String(usersPageSize));
    qs.set("skip", String((usersPage - 1) * usersPageSize));
    adminAxios.get(`/users?${qs.toString()}`).then((r) => { setUsers(r.data.items || []); setUsersTotal(r.data.total || 0); }).catch((e) => toast.error(formatErr(e)));
  }, [isSuperAdmin, usersPage, usersPageSize]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const payload = {
        business_name: settings.business_name,
        tagline: settings.tagline,
        phone: settings.phone,
        whatsapp: settings.whatsapp,
        email: settings.email,
        gst: settings.gst,
        completion_bonus: Number(settings.completion_bonus ?? 200),
        cities: settings.cities || [],
      };
      const { data } = await adminAxios.patch("/settings", payload);
      setSettings(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSavingSettings(false);
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await adminAxios.post("/users", newUser);
      setUsers((u) => [data, ...u]);
      setNewUser({ name: "", email: "", role: "support", password: "" });
      toast.success("Admin user created");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setCreating(false);
    }
  };

  const toggleUserActive = async (u) => {
    try {
      const { data } = await adminAxios.patch(`/users/${u.id}`, { active: !u.active });
      setUsers((arr) => arr.map((x) => (x.id === u.id ? data : x)));
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  if (!settings) return <AdminLayout title="Settings"><div className="text-slate-500">Loading…</div></AdminLayout>;

  return (
    <AdminLayout title="Settings">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Business Info */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-display font-bold text-sm uppercase tracking-wider text-slate-500 mb-4">Business Information</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Business name">
              <input data-testid="settings-business-name" value={settings.business_name || ""} onChange={(e) => setSettings((s) => ({ ...s, business_name: e.target.value }))} className={inputCls} />
            </Field>
            <Field label="Tagline">
              <input data-testid="settings-tagline" value={settings.tagline || ""} onChange={(e) => setSettings((s) => ({ ...s, tagline: e.target.value }))} className={inputCls} />
            </Field>
            <Field label="Phone">
              <input data-testid="settings-phone" value={settings.phone || ""} onChange={(e) => setSettings((s) => ({ ...s, phone: e.target.value }))} className={inputCls} />
            </Field>
            <Field label="WhatsApp">
              <input data-testid="settings-whatsapp" value={settings.whatsapp || ""} onChange={(e) => setSettings((s) => ({ ...s, whatsapp: e.target.value }))} className={inputCls} />
            </Field>
            <Field label="Email">
              <input data-testid="settings-email" value={settings.email || ""} onChange={(e) => setSettings((s) => ({ ...s, email: e.target.value }))} className={inputCls} />
            </Field>
            <Field label="GST number">
              <input data-testid="settings-gst" value={settings.gst || ""} onChange={(e) => setSettings((s) => ({ ...s, gst: e.target.value }))} className={inputCls} />
            </Field>
            <Field label="Technician earning for future completed jobs (₹)">
              <input
                type="number"
                min="0"
                step="10"
                data-testid="settings-completion-bonus"
                value={settings.completion_bonus ?? 200}
                onChange={(e) => setSettings((s) => ({ ...s, completion_bonus: Number(e.target.value || 0) }))}
                className={inputCls}
              />
              <p className="mt-1 text-[11px] text-slate-500">This value is used as the default for newly completed jobs only. Historical earnings remain unchanged.</p>
            </Field>
          </div>
          <button
            onClick={saveSettings}
            disabled={savingSettings}
            data-testid="settings-save"
            className="mt-5 inline-flex items-center gap-2 bg-[#1A3C6E] hover:bg-[#132D53] text-white font-semibold px-5 py-2.5 rounded-lg disabled:opacity-60"
          >
            <Save className="w-4 h-4" /> {savingSettings ? "Saving…" : "Save changes"}
          </button>

          <div className="mt-6 pt-5 border-t border-slate-100">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Service cities</h4>
            <div className="flex flex-wrap gap-2">
              {(settings.cities || []).map((c) => (
                <span key={c} className="flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 capitalize">
                  <span className="capitalize">{c}</span>
                  <button type="button" onClick={() => setSettings((s) => ({ ...s, cities: (s.cities || []).filter((x) => x !== c) }))} className="text-xs text-red-600">×</button>
                </span>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <input value={newCity} onChange={(e) => setNewCity(e.target.value)} placeholder="Add city" className={inputCls + " w-auto"} />
              <button type="button" onClick={() => {
                const v = (newCity || "").trim().toLowerCase();
                if (!v) return;
                if ((settings.cities || []).includes(v)) { toast.error("City already added"); return; }
                setSettings((s) => ({ ...s, cities: [...(s.cities || []), v] }));
                setNewCity("");
              }} className="inline-flex items-center gap-2 bg-[#1A3C6E] hover:bg-[#132D53] text-white font-semibold px-3 py-2 rounded-lg">Add</button>
            </div>
            <p className="text-xs text-slate-400 mt-2">Manage which cities the service is available in. Click Save changes to persist.</p>
          </div>
        </div>

        {/* Admin Users (super_admin only) */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-display font-bold text-sm uppercase tracking-wider text-slate-500 mb-4">Admin Users</h3>
          {!isSuperAdmin ? (
            <p className="text-sm text-slate-500">Only super-admins can manage admin users.</p>
          ) : (
            <>
              <div className="space-y-2 mb-5" data-testid="admin-users-list">
                {users.map((u) => (
                  <div key={u.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-100" data-testid={`user-row-${u.email}`}>
                    <div>
                      <div className="text-sm font-semibold text-slate-900">{u.name}</div>
                      <div className="text-xs text-slate-500">{u.email}</div>
                      <div className="text-[10px] uppercase tracking-wider font-semibold mt-0.5 inline-block px-2 py-0.5 rounded-full bg-[#1A3C6E]/10 text-[#1A3C6E]">
                        {u.role.replace(/_/g, " ")}
                      </div>
                    </div>
                    {u.role !== "super_admin" && (
                      <button
                        onClick={() => toggleUserActive(u)}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-md ${u.active ? "bg-slate-100 text-slate-700" : "bg-[#22C55E] text-white"}`}
                      >
                        {u.active ? "Deactivate" : "Activate"}
                      </button>
                    )}
                  </div>
                ))}
              </div>
                <div className="flex items-center justify-between">
                  <div className="text-sm text-slate-500">Showing {Math.min((usersPage-1)*usersPageSize+1, usersTotal)} - {Math.min(usersPage*usersPageSize, usersTotal)} of {usersTotal}</div>
                  <div className="flex items-center gap-2">
                    <button disabled={usersPage <= 1} onClick={() => setUsersPage((p) => Math.max(1, p-1))} className="px-3 py-1 rounded-md border">Prev</button>
                    <button disabled={(usersPage*usersPageSize) >= usersTotal} onClick={() => setUsersPage((p) => p+1)} className="px-3 py-1 rounded-md border">Next</button>
                    <select value={usersPageSize} onChange={(e) => { setUsersPageSize(Number(e.target.value)); setUsersPage(1); }} className="px-2 py-1 border rounded">
                      {[10,25,50,100].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                </div>

              <form onSubmit={createUser} className="border-t border-slate-100 pt-5" data-testid="new-user-form">
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Add new user</h4>
                <input data-testid="new-user-name" required placeholder="Name" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} className={inputCls + " mb-2"} />
                <input data-testid="new-user-email" required type="email" placeholder="Email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className={inputCls + " mb-2"} />
                <input data-testid="new-user-password" required type="password" placeholder="Password (min 8 chars)" minLength={8} value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} className={inputCls + " mb-2"} />
                <select data-testid="new-user-role" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })} className={inputCls + " mb-3 bg-white"}>
                  {ROLES.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
                </select>
                <button type="submit" disabled={creating} data-testid="new-user-submit" className="w-full inline-flex items-center justify-center gap-2 bg-[#FF6B2B] hover:bg-[#E65A20] text-white font-semibold py-2.5 rounded-lg disabled:opacity-60">
                  <UserPlus className="w-4 h-4" /> {creating ? "Creating…" : "Create user"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>

      {/* <div className="mt-6 bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-display font-bold text-sm uppercase tracking-wider text-slate-500 mb-2">Phase 2 — Coming soon</h3>
        <p className="text-sm text-slate-600">WhatsApp Notifications Center, full Reports & Exports, Razorpay AMC checkout, Maps autocomplete & real KYC uploads.</p>
      </div> */}
    </AdminLayout>
  );
}

const inputCls = "w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-[#1A3C6E] focus:ring-2 focus:ring-[#1A3C6E]/10 outline-none";

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs font-semibold text-slate-600">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);
